import { ProtocolVersion } from '@uniswap/client-pools/dist/pools/v1/types_pb'
import { ADDRESS_ZERO } from '@uniswap/router-sdk'
import { CurrencyAmount, Token } from '@uniswap/sdk-core'
import { createCollectFeesStep } from 'uniswap/src/features/transactions/liquidity/steps/collectFees'
import { orderCollectFeesSteps } from 'uniswap/src/features/transactions/liquidity/steps/collectFeesSteps'
import { createCollectLpIncentiveRewardsStep } from 'uniswap/src/features/transactions/liquidity/steps/collectIncentiveRewards'
import { orderCollectLpIncentiveRewardsSteps } from 'uniswap/src/features/transactions/liquidity/steps/collectIncentiveRewardsSteps'
import { orderDecreaseLiquiditySteps } from 'uniswap/src/features/transactions/liquidity/steps/decreaseLiquiditySteps'
import { createDecreasePositionStep } from 'uniswap/src/features/transactions/liquidity/steps/decreasePosition'
import { orderIncreaseLiquiditySteps } from 'uniswap/src/features/transactions/liquidity/steps/increaseLiquiditySteps'
import {
  createCreatePositionAsyncStep,
  createIncreasePositionAsyncStep,
  createIncreasePositionStep,
  createInitializePoolStep,
  createSubscribePositionAsyncStep,
} from 'uniswap/src/features/transactions/liquidity/steps/increasePosition'
import {
  createMigratePositionAsyncStep,
  createMigratePositionStep,
} from 'uniswap/src/features/transactions/liquidity/steps/migrate'
import { orderMigrateLiquiditySteps } from 'uniswap/src/features/transactions/liquidity/steps/migrationSteps'
import { DELI_HOOK_CP_ADDRESS } from 'uniswap/src/features/transactions/liquidity/steps/shared'
import {
  createStakeBmxApprovalStep,
  createStakeBmxTransactionStep,
} from 'uniswap/src/features/transactions/liquidity/steps/stakeAfterClaim'
import {
  LiquidityTransactionType,
  LiquidityTxAndGasInfo,
  isValidLiquidityTxContext,
} from 'uniswap/src/features/transactions/liquidity/types'
import { createApprovalTransactionStep } from 'uniswap/src/features/transactions/steps/approve'
import { createPermit2SignatureStep } from 'uniswap/src/features/transactions/steps/permit2Signature'
import { createPermit2TransactionStep } from 'uniswap/src/features/transactions/steps/permit2Transaction'
import { createRevocationTransactionStep } from 'uniswap/src/features/transactions/steps/revoke'
import { TransactionStep, TransactionStepType } from 'uniswap/src/features/transactions/steps/types'
import type { PermitTypedData } from 'uniswap/src/features/transactions/swap/types/swapTxAndGasInfo'
import { PermitMethod } from 'uniswap/src/features/transactions/swap/types/swapTxAndGasInfo'
import { logger } from 'utilities/src/logger/logger'

const V4_APPROVAL_TOOLTIP_TEXT =
  "Creating a v4 position requests a one-time max allowance per token via Uniswap's Permit2. The approval is to Uniswap contracts and can be revoked later in your wallet."

export function generateLPTransactionSteps(txContext: LiquidityTxAndGasInfo): TransactionStep[] {
  const isValidLP = isValidLiquidityTxContext(txContext)

  if (isValidLP) {
    if (txContext.type === LiquidityTransactionType.Collect) {
      return orderCollectFeesSteps({
        collectFees: createCollectFeesStep(txContext.txRequest),
      })
    }

    if (txContext.type === LiquidityTransactionType.ClaimIncentives) {
      const stakeConfig = txContext.stakeAfterClaim
      const claimSteps = txContext.claimRequests.map((req) =>
        createCollectLpIncentiveRewardsStep(req.txRequest, {
          rewardSymbol: req.rewardSymbol,
          gauge: req.gauge,
          rewardTokenAddress: req.rewardTokenAddress,
          stakeAfterClaimAccumulator: stakeConfig?.accumulator,
          isBmxReward: req.isBmx,
          stakeBmxToken: stakeConfig?.bmxToken,
        }),
      )
      const orderedClaims = orderCollectLpIncentiveRewardsSteps({ claims: claimSteps })
      if (!stakeConfig) {
        return orderedClaims as TransactionStep[]
      }
      return [...orderedClaims, createStakeBmxApprovalStep(stakeConfig), createStakeBmxTransactionStep(stakeConfig)]
    }

    if (txContext.type === LiquidityTransactionType.Subscribe) {
      return [
        {
          type: TransactionStepType.SubscribePositionTransaction,
          txRequest: txContext.txRequest,
        } as any,
      ]
    }

    const base = txContext as any
    const {
      action,
      approveToken0Request,
      approveToken1Request,
      approvePositionTokenRequest,
      token0PermitTransaction,
      token1PermitTransaction,
      positionTokenPermitTransaction,
    } = base

    const revokeToken0 = createRevocationTransactionStep(
      base.revokeToken0Request,
      action.currency0Amount.currency.wrapped,
    )
    const revokeToken1 = createRevocationTransactionStep(
      base.revokeToken1Request,
      action.currency1Amount.currency.wrapped,
    )
    const approvalToken0 = createApprovalTransactionStep(approveToken0Request, action.currency0Amount)
    const approvalToken1 = createApprovalTransactionStep(approveToken1Request, action.currency1Amount)
    const approvalPositionToken = createApprovalTransactionStep(
      approvePositionTokenRequest,
      action.liquidityToken ? CurrencyAmount.fromRawAmount(action.liquidityToken, 1) : undefined,
      [action.currency0Amount.currency, action.currency1Amount.currency],
    )

    const token0PermitTransactionStep = createPermit2TransactionStep(token0PermitTransaction, action.currency0Amount, [
      action.currency0Amount.currency,
      action.currency1Amount.currency,
    ])

    const token1PermitTransactionStep = createPermit2TransactionStep(token1PermitTransaction, action.currency1Amount, [
      action.currency0Amount.currency,
      action.currency1Amount.currency,
    ])

    const positionTokenPermitTransactionStep = createPermit2TransactionStep(
      positionTokenPermitTransaction,
      action.currency1Amount,
      [action.currency0Amount.currency, action.currency1Amount.currency],
    )

    switch (txContext.type) {
      case 'decrease':
        return orderDecreaseLiquiditySteps({
          approvalPositionToken,
          decreasePosition: createDecreasePositionStep(base.txRequest),
        })
      case 'migrate':
        if (base.unsigned) {
          return orderMigrateLiquiditySteps({
            permit: createPermit2SignatureStep(
              base.permit.typedData,
              new Token(1, ADDRESS_ZERO, 1, 'Uniswap V3 Positions NFT-V1', 'Uniswap V3 Positions NFT-V1'),
            ),
            migrate: createMigratePositionAsyncStep(
              base.migratePositionRequestArgs,
              base.permit.typedData.values.deadline as number,
            ),
            positionTokenPermitTransaction: undefined,
          })
        } else {
          return orderMigrateLiquiditySteps({
            permit: undefined,
            positionTokenPermitTransaction: positionTokenPermitTransactionStep,
            migrate: createMigratePositionStep(base.txRequest),
          })
        }
      case 'create':
      case 'increase':
        const initializePool =
          txContext.type === LiquidityTransactionType.Create
            ? createInitializePoolStep(base.initializePoolRequest)
            : undefined
        const initializationOnly = Boolean(initializePool && !txContext.txRequest)
        if (initializationOnly && initializePool) {
          return [initializePool]
        }
        const increaseRequest = base.txRequest
        if (!increaseRequest) {
          return initializePool ? [initializePool] : []
        }
        if (base.unsigned) {
          const permitTypedData = base.permit as PermitTypedData | undefined
          if (!permitTypedData || permitTypedData.method !== PermitMethod.TypedData) {
            return initializePool ? [initializePool] : []
          }
          const isV4 = txContext.protocolVersion === ProtocolVersion.V4
          const isCreateFlow = txContext.type === LiquidityTransactionType.Create
          const hooks = (base?.createPositionRequestArgs?.position?.pool?.hooks as string | undefined) ?? undefined
          const nonCpHook = (hooks?.toLowerCase() ?? '') !== DELI_HOOK_CP_ADDRESS.toLowerCase()
          const shouldShowV4Tooltip = Boolean(isV4 && isCreateFlow && nonCpHook)

          if (shouldShowV4Tooltip) {
            if (approvalToken0) {
              approvalToken0.infoTooltipText = V4_APPROVAL_TOOLTIP_TEXT
            }
            if (approvalToken1) {
              approvalToken1.infoTooltipText = V4_APPROVAL_TOOLTIP_TEXT
            }
          }

          const subscribeStep = shouldShowV4Tooltip
            ? createSubscribePositionAsyncStep({
                chainId: Number(base?.createPositionRequestArgs?.chainId),
                walletAddress: base?.createPositionRequestArgs?.walletAddress as `0x${string}`,
              })
            : undefined

          const permitStep = createPermit2SignatureStep(
            permitTypedData.typedData,
            action.currency0Amount.currency,
            shouldShowV4Tooltip ? { infoTooltipText: V4_APPROVAL_TOOLTIP_TEXT } : undefined,
          ) // TODO: what about for multiple tokens

          const steps = orderIncreaseLiquiditySteps({
            revokeToken0,
            revokeToken1,
            approvalToken0,
            approvalToken1,
            approvalPositionToken,
            initializePool,
            permit: permitStep,
            token0PermitTransaction: undefined,
            token1PermitTransaction: undefined,
            increasePosition:
              txContext.type === 'increase'
                ? createIncreasePositionAsyncStep(txContext.increasePositionRequestArgs)
                : createCreatePositionAsyncStep(txContext.createPositionRequestArgs),
            subscribePosition: subscribeStep as any,
          })
          try {
            logger.info('generateLPTransactionSteps', 'unsignedFlow', 'lp:steps', { types: steps.map((s) => s.type) })
          } catch {}
          return steps
        } else {
          const isV4b = txContext.protocolVersion === ProtocolVersion.V4
          const hooks =
            (base?.createPositionRequestArgs?.position?.pool?.hooks as string | undefined) ??
            (base?.increasePositionRequestArgs?.position?.pool?.hooks as string | undefined)
          const nonCpHookB = (hooks?.toLowerCase() ?? '') !== DELI_HOOK_CP_ADDRESS.toLowerCase()
          const isCreateOrNew =
            txContext.type === LiquidityTransactionType.Create ||
            (txContext.type === LiquidityTransactionType.Increase && !base?.increasePositionRequestArgs?.tokenId)
          if (isV4b && txContext.type === LiquidityTransactionType.Create && nonCpHookB) {
            if (approvalToken0) {
              approvalToken0.infoTooltipText = V4_APPROVAL_TOOLTIP_TEXT
            }
            if (approvalToken1) {
              approvalToken1.infoTooltipText = V4_APPROVAL_TOOLTIP_TEXT
            }
          }
          const subscribeStep =
            isV4b && nonCpHookB && isCreateOrNew
              ? createSubscribePositionAsyncStep({
                  chainId: Number(base.txRequest?.chainId),
                  walletAddress: (base.txRequest?.from ?? ADDRESS_ZERO) as `0x${string}`,
                })
              : undefined

          const steps = orderIncreaseLiquiditySteps({
            revokeToken0,
            revokeToken1,
            approvalToken0,
            approvalToken1,
            approvalPositionToken,
            initializePool,
            permit: undefined,
            token0PermitTransaction: token0PermitTransactionStep,
            token1PermitTransaction: token1PermitTransactionStep,
            increasePosition: createIncreasePositionStep(increaseRequest),
            subscribePosition: subscribeStep as any,
          })
          try {
            logger.info('generateLPTransactionSteps', 'signedFlow', 'lp:steps', { types: steps.map((s) => s.type) })
          } catch {}
          return steps
        }
    }
  }

  return []
}
